import React from "react";
import { ownerApi } from "../../../../api/owner";
import { createOwnerJarvisBriefing } from "./ownerJarvisUtils";
import { executeOwnerJarvisActionPlan, ownerJarvisActionExecutionFailureReport, ownerJarvisActionExecutionNotice } from "./ownerJarvisUiExecutor";
import { ownerJarvisConversationContext } from "./ownerJarvisConversationJournal";
import { persistOwnerJarvisTurn, readOwnerJarvisStoredTurn } from "./ownerJarvisStorage";

function activeSession() {
  return readOwnerJarvisStoredTurn()?.session || null;
}

export function useOwnerJarvisRuntime({ navigate }) {
  const introMessage = React.useMemo(() => createOwnerJarvisBriefing(), []);

  const onUnhandledUtterance = React.useCallback(async (input) => {
    const raw = String(input?.raw || input?.text || "").trim();
    if (!raw) return { handled: false };

    try {
      const currentPath = `${window.location.pathname || ""}${window.location.search || ""}`;
      const conversationContext = ownerJarvisConversationContext();
      const inputSource = input?.inputSource || "voice";
      const response = await ownerApi.jarvisTurn({
        message: raw,
        session: activeSession(),
        inputSource,
        currentPath,
        conversationContext,
        runtimeContext: {
          authority: "backend",
          frontendInterpreter: false,
        },
      });
      const turn = response?.turn || null;
      if (!turn) return { handled: false };

      persistOwnerJarvisTurn(turn?.session ? turn : null);
      const execution = executeOwnerJarvisActionPlan(navigate, turn);
      const failureReport = ownerJarvisActionExecutionFailureReport(turn, execution, {
        utterance: raw,
        currentPath,
        inputSource,
        conversationContext,
      });
      if (failureReport) ownerApi.reportJarvisUnhandled(failureReport).catch(() => undefined);
      const executionNotice = ownerJarvisActionExecutionNotice(execution);
      if (turn.refreshOwner) {
        try {
          window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", {
            detail: { scopes: ["today", "projects", "finance"] },
          }));
        } catch { /* optional */ }
      }
      try {
        window.dispatchEvent(new CustomEvent("owner:jarvis-ai-interpreted", { detail: { turn } }));
      } catch { /* optional */ }

      return {
        handled: turn.handled !== false,
        message: [turn.reply, turn.question, executionNotice].filter(Boolean).join(" "),
        highlightSection: turn.highlightSection || "today-jarvis",
        ai: turn.ai || null,
      };
    } catch {
      return {
        handled: true,
        message: "No pude procesarlo en Jarvis ahora. No ejecuté ninguna acción; probá de nuevo.",
        highlightSection: "today-jarvis",
      };
    }
  }, [navigate]);

  const hasPriorityConversation = React.useCallback(() => Boolean(activeSession()), []);

  const resetConversationState = React.useCallback(() => {
    persistOwnerJarvisTurn(null);
    try { window.sessionStorage.removeItem("wp-owner-day-pending-selection-until"); } catch { /* optional */ }
  }, []);

  return {
    introMessage,
    onUnhandledUtterance,
    hasPriorityConversation,
    resetConversationState,
  };
}
